import { FC, useEffect, useMemo, useState } from "react";
import { Select } from "antd";

import { TV_KIND_LABELS, TV_STATUS_LABELS, TvVideo } from "../Lib";
import { getTvVideo, searchTvVideos } from "../Providers/tv-api";
import { useDebouncedSearch } from "./useDebouncedSearch";

export const videoLabel = (v: TvVideo) =>
  `№${v.id} · ${v.title} · ${TV_KIND_LABELS[v.kind]}${
    v.status !== "PUBLISHED" ? ` · ${TV_STATUS_LABELS[v.status]}` : ""
  }`;

type Props = {
  value?: number | null;
  onChange?: (value: number | null, video?: TvVideo) => void;
  placeholder?: string;
  /** id, которые уже выбраны (например, в подборке) — не предлагаем. */
  exclude?: number[];
  /** только выпуски этого шоу */
  showId?: number;
};

/** Выбор видео: поиск по названию или номеру. */
export const VideoPicker: FC<Props> = ({
  value,
  onChange,
  placeholder = "Название или номер видео",
  exclude = [],
  showId,
}) => {
  const { items, loading, error, setText } = useDebouncedSearch(
    (text: string) => searchTvVideos(text, showId),
    String(showId ?? "")
  );
  const [current, setCurrent] = useState<TvVideo>();

  // Для уже выбранного видео подгружаем название, чтобы не показывать голый номер.
  useEffect(() => {
    if (value && current?.id !== value) {
      getTvVideo(value)
        .then(setCurrent)
        .catch(() => setCurrent(undefined));
    }
  }, [value, current?.id]);

  const options = useMemo(() => {
    const list = items.filter((v) => !exclude.includes(v.id));
    if (
      current &&
      current.id === value &&
      !list.some((v) => v.id === current.id)
    ) {
      list.unshift(current);
    }
    return list.map((v) => ({ value: v.id, label: videoLabel(v), video: v }));
  }, [items, current, value, exclude]);

  return (
    <Select
      showSearch
      allowClear
      style={{ width: "100%" }}
      value={value ?? undefined}
      placeholder={placeholder}
      filterOption={false}
      onSearch={setText}
      loading={loading}
      notFoundContent={error ?? (loading ? "Ищем…" : "Видео не найдены")}
      options={options}
      onChange={(v, option) =>
        onChange?.(
          (v as number) ?? null,
          (option as { video?: TvVideo } | undefined)?.video
        )
      }
    />
  );
};
